import {
  CardWithIcon,
  ContentSection,
  Heading,
  Text,
  TextLink,
} from '@cinch-labs/design-system'
import { useViewport } from '@cinch-labs/shared-util'
import { TrackingEventTypes, useAnalytics } from '@cinch-nx/shared-analytics'

import TitleWithLogo from '../../title-with-logo/title-with-logo'
import styles from './how-to-get-cinchcharge.module.css'

const HowToGetCinchCharge: React.FC = () => {
  const { vw } = useViewport({
    updateOnResize: true,
    updateOnOrientationChange: true,
  })
  const isMobile = vw < 1024

  const IS_MOBILE_APP =
    typeof window !== 'undefined' &&
    window.navigator?.userAgent.includes('cinchapp')

  const { trackEvent } = useAnalytics()

  return (
    <section
      className={styles.container}
      data-testid="feature-icon-card-section"
    >
      <div className={styles.innerContainer}>
        <div className={styles.titleWrapper}>
          <TitleWithLogo
            headingLevel={'h3'}
            leftText={'How to get'}
            textClassName={styles.title}
            logo={'cinchChargePurple'}
            logoHeight={isMobile ? '28' : '36'}
            logoWidth={isMobile ? '142' : '182'}
          />
        </div>

        <div className={styles.cardsWrapper}>
          <div
            data-testid="single-icon-card"
            className={styles.singleCardWrapper}
          >
            <CardWithIcon
              iconName={'vehicle-side'}
              iconGradient="galaxy"
              iconSize={32}
            />
            <Text className={styles.stepNumber}>{'STEP 1'}</Text>
            <Heading className={styles.cardHeading}>
              Buy a fully electric car
            </Heading>
            <ContentSection
              sectionWrapperClass={styles.sectionWrapper}
              classNames={{ body: styles.cardBody }}
            >
              With our wide range of&nbsp;
              <TextLink
                href="/used-cars/electric-fuel-type"
                target={IS_MOBILE_APP ? '' : '_blank'}
                className={styles.linkText}
                onClick={() => {
                  trackEvent({
                    eventName: `cinchCharge landing page`,
                    data: {
                      event: {
                        name: 'click',
                        category: 'How to get cinchCharge',
                        action: 'internal link',
                        label: 'electric cars',
                        value:
                          'https://www.cinch.co.uk/used-cars/electric-fuel-type',
                      },
                    },
                    type: TrackingEventTypes.ADOBE,
                  })
                }}
              >
                electric cars
              </TextLink>
              , you’re sure to find the one.
            </ContentSection>
          </div>

          <div
            data-testid="single-icon-card"
            className={styles.singleCardWrapper}
          >
            <CardWithIcon
              iconName={'card'}
              iconGradient="galaxy"
              iconSize={32}
            />
            <Text className={styles.stepNumber}>{'STEP 2'}</Text>
            <Heading className={styles.cardHeading}>
              Check your glovebox
            </Heading>
            <ContentSection
              sectionWrapperClass={styles.sectionWrapper}
              classNames={{ body: styles.cardBody }}
            >
              Your cinchCharge card should be delivered with the car.
            </ContentSection>
          </div>

          <div
            data-testid="single-icon-card"
            className={styles.singleCardWrapper}
          >
            <CardWithIcon
              iconName={'electric'}
              iconGradient="galaxy"
              iconSize={32}
            />
            <Text className={styles.stepNumber}>{'STEP 3'}</Text>
            <Heading className={styles.cardHeading}>Download the app</Heading>
            <ContentSection
              sectionWrapperClass={styles.sectionWrapper}
              classNames={{ body: styles.cardBody }}
            >
              Search for ‘cinchCharge’ on&nbsp;
              <TextLink
                href="https://play.google.com/store/apps/details?id=com.pauatech.cinchCharge"
                target="_blank"
                className={styles.linkText}
                onClick={() => {
                  trackEvent({
                    eventName: `cinchCharge landing page`,
                    data: {
                      event: {
                        name: 'click',
                        category: 'How to get cinchCharge',
                        action: 'external link',
                        label: 'Google Play',
                        value:
                          'https://play.google.com/store/apps/details?id=com.pauatech.cinchCharge',
                      },
                    },
                    type: TrackingEventTypes.ADOBE,
                  })
                }}
              >
                Google Play
              </TextLink>
              &nbsp;or in the&nbsp;
              <TextLink
                href="https://apps.apple.com/gb/app/id1640016085"
                target="_blank"
                className={styles.linkText}
                onClick={() => {
                  trackEvent({
                    eventName: `cinchCharge landing page`,
                    data: {
                      event: {
                        name: 'click',
                        category: 'How to get cinchCharge',
                        action: 'external link',
                        label: 'App Store',
                        value: 'https://apps.apple.com/gb/app/id1640016085',
                      },
                    },
                    type: TrackingEventTypes.ADOBE,
                  })
                }}
              >
                App Store
              </TextLink>
              .
            </ContentSection>
          </div>

          <div
            data-testid="single-icon-card"
            className={styles.singleCardWrapper}
          >
            <CardWithIcon
              iconName={'cvc'}
              iconGradient="galaxy"
              iconSize={32}
            />
            <Text className={styles.stepNumber}>{'STEP 4'}</Text>
            <Heading className={styles.cardHeading}>
              Register your details
            </Heading>
            <ContentSection
              sectionWrapperClass={styles.sectionWrapper}
              classNames={{ body: styles.cardBody }}
            >
              Follow the instructions to set up your account and link a payment
              card.
            </ContentSection>
          </div>
        </div>

        <ContentSection
          sectionWrapperClass={styles.bodySectionWrapper}
          classNames={{ body: styles.body }}
        >
          Already bought a fully electric car from cinch? You can benefit from
          cinchCharge too. Just&nbsp;
          <TextLink
            href="./charging-electric-cars/card-request"
            className={styles.linkText}
            onClick={() => {
              trackEvent({
                eventName: `cinchCharge landing page`,
                data: {
                  event: {
                    name: 'click',
                    category: 'How to get cinchCharge',
                    action: 'internal link',
                    label: 'request a card',
                    value:
                      'cinch.co.uk/car-care/charging-electric-cars/card-request',
                  },
                },
                type: TrackingEventTypes.ADOBE,
              })
            }}
          >
            request a card
          </TextLink>
          &nbsp;using our quick online form.
        </ContentSection>
      </div>
    </section>
  )
}

export default HowToGetCinchCharge
